import React, { useState, useRef, useEffect } from 'react';

export default function ImageCropper({ imageFile, aspectRatio, onComplete, onCancel, maxWidth, maxHeight }) {
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        // Initialize crop in center
        const initialSize = Math.min(img.width, img.height) * 0.8;
        const width = aspectRatio ? initialSize : initialSize;
        const height = aspectRatio ? initialSize / aspectRatio : initialSize;
        setCrop({
          x: (img.width - width) / 2,
          y: (img.height - height) / 2,
          width,
          height
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile, aspectRatio]);

  useEffect(() => {
    if (image && canvasRef.current) {
      drawCanvas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, crop]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = image.width;
    canvas.height = image.height;

    // Draw image
    ctx.drawImage(image, 0, 0);

    // Draw overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Clear crop area
    ctx.clearRect(crop.x, crop.y, crop.width, crop.height);
    ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, crop.x, crop.y, crop.width, crop.height);

    // Draw crop border
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x, crop.y, crop.width, crop.height);

    // Draw corner handles
    const handleSize = 10;
    ctx.fillStyle = '#fff';
    ctx.fillRect(crop.x - handleSize / 2, crop.y - handleSize / 2, handleSize, handleSize);
    ctx.fillRect(crop.x + crop.width - handleSize / 2, crop.y - handleSize / 2, handleSize, handleSize);
    ctx.fillRect(crop.x - handleSize / 2, crop.y + crop.height - handleSize / 2, handleSize, handleSize);
    ctx.fillRect(crop.x + crop.width - handleSize / 2, crop.y + crop.height - handleSize / 2, handleSize, handleSize);
  };

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * image.width;
    const y = ((e.clientY - rect.top) / rect.height) * image.height;

    if (x >= crop.x && x <= crop.x + crop.width && y >= crop.y && y <= crop.y + crop.height) {
      setIsDragging(true);
      setDragStart({ x: x - crop.x, y: y - crop.y });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * image.width;
    const y = ((e.clientY - rect.top) / rect.height) * image.height;

    setCrop(prev => ({
      ...prev,
      x: Math.max(0, Math.min(image.width - prev.width, x - dragStart.x)),
      y: Math.max(0, Math.min(image.height - prev.height, y - dragStart.y))
    }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCropResize = (delta) => {
    setCrop(prev => {
      let newWidth = prev.width + delta;
      let newHeight = aspectRatio ? newWidth / aspectRatio : prev.height + delta;

      // Constrain to image bounds
      newWidth = Math.min(newWidth, image.width - prev.x);
      newHeight = Math.min(newHeight, image.height - prev.y);
      newWidth = Math.max(50, newWidth);
      newHeight = Math.max(50, newHeight);

      if (aspectRatio) {
        newHeight = newWidth / aspectRatio;
      }

      return { ...prev, width: newWidth, height: newHeight };
    });
  };

  const getCroppedImage = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Calculate output size
    let outputWidth = crop.width;
    let outputHeight = crop.height;

    // Resize if needed
    if (maxWidth && outputWidth > maxWidth) {
      outputHeight = (outputHeight / outputWidth) * maxWidth;
      outputWidth = maxWidth;
    }
    if (maxHeight && outputHeight > maxHeight) {
      outputWidth = (outputWidth / outputHeight) * maxHeight;
      outputHeight = maxHeight;
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Draw cropped and resized image
    ctx.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], imageFile.name, { type: 'image/jpeg', lastModified: Date.now() });
        resolve(file);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleComplete = async () => {
    const croppedFile = await getCroppedImage();
    onComplete(croppedFile);
  };

  if (!image) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading image...</div>;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#2c2c2c',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <h3 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>Crop & Resize Image</h3>

        <div style={{
          position: 'relative',
          overflow: 'auto',
          maxHeight: '60vh',
          backgroundColor: '#1a1a1a',
          borderRadius: '4px'
        }}>
          <canvas
            ref={canvasRef}
            style={{
              display: 'block',
              maxWidth: '100%',
              height: 'auto',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', color: '#fff' }}>
          <label style={{ fontSize: '14px' }}>Crop Size:</label>
          <button
            onClick={() => handleCropResize(-20)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            -
          </button>
          <span style={{ fontSize: '14px' }}>
            {Math.round(crop.width)} × {Math.round(crop.height)}
          </span>
          <button
            onClick={() => handleCropResize(20)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            +
          </button>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--btn-secondary)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--success)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}
